<template>
  <dialog v-if="open" class="modal modal-open" @close="open = false">
    <div class="modal-box max-w-lg">
      <h3 class="font-bold text-lg mb-3">Version</h3>

      <div class="text-sm space-y-2">
        <div>
          <div class="font-semibold opacity-70">Built</div>
          <div class="font-mono break-all">{{ built.version }}</div>
          <div class="opacity-70 text-xs">
            branch <span class="font-mono">{{ built.branch }}</span> · {{ built.builtAt }}
          </div>
        </div>

        <div>
          <div class="font-semibold opacity-70">Deployed</div>
          <div v-if="loading" class="opacity-70">Loading…</div>
          <div v-else-if="!deployed" class="opacity-70">No manifest at /version.json</div>
          <template v-else>
            <div class="font-mono break-all">{{ deployed.version }}</div>
            <div class="opacity-70 text-xs">
              branch <span class="font-mono">{{ deployed.branch }}</span> · {{ deployed.builtAt }}
            </div>
            <div v-if="deployed.sha !== built.sha" class="text-warning text-xs mt-1">
              Deployed build differs from this tab — refresh to update.
            </div>
          </template>
        </div>

        <div class="divider my-1"></div>

        <div>
          <div class="font-semibold opacity-70">API</div>
          <div v-if="apiLoading" class="opacity-70">Loading…</div>
          <div v-else-if="!apiVersion" class="opacity-70">Unavailable</div>
          <div v-else class="font-mono break-all">{{ apiVersion }}</div>
        </div>
      </div>

      <div class="modal-action">
        <button class="btn btn-sm" @click="copy">{{ copied ? 'Copied' : 'Copy' }}</button>
        <button class="btn btn-sm btn-primary" @click="open = false">Close</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="open = false">close</button>
    </form>
  </dialog>
</template>

<script lang="ts">
import axios from 'axios';
import { authService } from '@/services/api';

interface VersionManifest {
  version: string;
  sha: string;
  buildNumber: string;
  builtAt: string;
  branch: string;
}

export default {
  name: 'VersionDebugPanel',
  data() {
    return {
      open: false,
      copied: false,
      loading: false,
      apiLoading: false,
      apiVersion: null as string | null,
      deployed: null as VersionManifest | null,
      built: {
        version: __APP_VERSION__,
        sha: __BUILD_SHA__,
        buildNumber: __BUILD_NUMBER__,
        builtAt: __BUILD_TIME__,
        branch: __BUILD_BRANCH__,
      } as VersionManifest,
    };
  },
  methods: {
    onKeydown(e: KeyboardEvent) {
      if (e.ctrlKey && e.shiftKey && (e.key === 'V' || e.key === 'v')) {
        e.preventDefault();
        this.open = !this.open;
        if (this.open) {
          this.fetchDeployed();
          this.fetchApiVersion();
        }
      }
    },
    async fetchApiVersion() {
      this.apiLoading = true;
      try {
        const user = await authService.getUserInfo();
        this.apiVersion = user.apiVersion ?? null;
      } catch {
        this.apiVersion = null;
      } finally {
        this.apiLoading = false;
      }
    },
    async fetchDeployed() {
      this.loading = true;
      this.copied = false;
      try {
        const base = import.meta.env.BASE_URL || '/';
        const { data } = await axios.get<VersionManifest>(`${base}version.json?t=${Date.now()}`, {
          headers: { 'Cache-Control': 'no-cache' },
        });
        this.deployed = data && typeof data === 'object' && typeof data.sha === 'string' ? data : null;
      } catch {
        this.deployed = null;
      } finally {
        this.loading = false;
      }
    },
    async copy() {
      const payload = JSON.stringify(
        { built: this.built, api: this.apiVersion, deployed: this.deployed },
        null,
        2,
      );
      try {
        await navigator.clipboard.writeText(payload);
        this.copied = true;
      } catch {
        this.copied = false;
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown);
  },
  unmounted() {
    window.removeEventListener('keydown', this.onKeydown);
  },
};
</script>
